import classnames from "classnames";

import { calculateNodeCoordinates } from "../../../utils";

const getNodeChildren = (node, nodeMap) => {
  switch (node?.node_type?.["@type"]) {
    case "aggregation":
      return node.node_type.children;
    case "binary_merge":
      return [node.node_type.left, node.node_type.right];
    case "subset_refinement":
      return [
        node.node_type.initial,
        ...Object.entries(node.node_type.correction).reduce(
          (acc, [key, value]) => (key === "@type" ? acc : [...acc, value]),
          []
        ),
      ];
    case "segment":
      return nodeMap[node.node_type.node]?.node_type?.children;
    case "segmented":
      return node.node_type.segments;
    default:
      return undefined;
  }
};

const getCommonNodeData = (
  node,
  levelIndex,
  totalLevels,
  nodeIndex,
  totalNodes,
  max
) => ({
  id: node.id,
  type: node.type || "default",
  position: calculateNodeCoordinates(
    levelIndex,
    totalLevels,
    nodeIndex,
    totalNodes,
    max
  ),
  sourcePosition: "right",
  targetPosition: "left",
});

const getColumnNodeData = node => ({
  data: { label: node.node_type?.column_name || "None" },
  selectable: true,
  className: "text-xs w-auto border bg-white",
});

const getBinaryMergeNodeData = (node, levelIndex) => ({
  data: { label: node.id?.replace("@", "") },
  selectable: true,
  className: classnames("text-xs w-auto border", {
    "bg-blue-100": levelIndex > 0,
    "bg-blue-300": levelIndex === 0,
  }),
});

const getSubsetRefinementNodeData = (node, levelIndex) => ({
  data: { label: node.id?.replace("@", "") },
  selectable: false,
  className: classnames("text-xs w-auto border", {
    "bg-red-100": levelIndex > 0,
    "bg-red-300": levelIndex === 0,
  }),
});

const getSegmentNodeData = node => ({
  data: { label: `Segment ${node.id?.replace("@", "")}` },
  selectable: false,
  className: "text-sm w-auto border bg-gray-300",
});

const getSegmentedNodeData = node => ({
  data: { label: `Segmented ${node.id?.replace("@", "")}` },
  selectable: false,
  className: "text-sm w-auto border bg-gray-300",
});

const getAggregateNodeData = (node, levelIndex) => ({
  data: { label: node.id?.replace("@", "") },
  selectable: false,
  className: classnames("text-xs w-auto border", {
    "bg-green-100": levelIndex > 0,
    "bg-green-300": levelIndex === 0,
  }),
});

const getNodeData = (
  node,
  levelIndex,
  totalLevels,
  nodeIndex,
  totalNodes,
  max
) => {
  const commonData = getCommonNodeData(
    node,
    levelIndex,
    totalLevels,
    nodeIndex,
    totalNodes,
    max
  );
  switch (node.node_type?.["@type"]) {
    case "column":
      return { ...commonData, ...getColumnNodeData(node) };
    case "binary_merge":
      return { ...commonData, ...getBinaryMergeNodeData(node, levelIndex) };
    case "subset_refinement":
      return {
        ...commonData,
        ...getSubsetRefinementNodeData(node, levelIndex),
      };
    case "segment":
      return { ...commonData, ...getSegmentNodeData(node) };
    case "segmented":
      return { ...commonData, ...getSegmentedNodeData(node) };
    case "aggregation":
      return { ...commonData, ...getAggregateNodeData(node, levelIndex) };
    default:
      return undefined;
  }
};

export { getNodeData, getNodeChildren };
